import { useContext, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { getRoomMembers } from '../api/rooms';
import { UserContext } from '../functions/Auth';

// mui
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

// icons
import HomeIcon from '@mui/icons-material/Home';
import FlagIcon from '@mui/icons-material/Flag';
import FaceIcon from '@mui/icons-material/Face';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import HelpIcon from '@mui/icons-material/Help';
import MovieCreationIcon from '@mui/icons-material/MovieCreation';
import SettingsIcon from '@mui/icons-material/Settings';

export default function GlobalNavigarion() {
    const location = useLocation();
    const roomId = location.pathname.split('/')[1];
    const isRoom = Boolean(roomId && /^\d+$/.test(roomId));
    const userInfo = useContext(UserContext);
    const roomInfo = userInfo?.rooms.find(room => room.roomId.toString() === roomId);

    const [roomMembers, setRoomMembers] = useState<{
        userId: number,
        userName: string,
    }[]>([]); // ルームメンバー

    useEffect(() => {
        if (isRoom) {
            (async () => {
                const response = await getRoomMembers(roomId);
                if (response) {
                    setRoomMembers(response);
                }
            })()

        }
    }, [roomId, isRoom]);

    return (
        <>
            <Box sx={{
                overflow: 'auto',
                'a': {
                    color: (theme) => theme.palette.common.black,
                    textDecoration: "none",
                }
            }}>
                <List>
                    <ListItem key="home" disablePadding component={NavLink} to={"/"}>
                        <ListItemButton selected={location.pathname === "/"}>
                            <ListItemIcon>
                                <HomeIcon />
                            </ListItemIcon>
                            <ListItemText primary="ホーム" />
                        </ListItemButton>
                    </ListItem>
                </List>
                <Divider />
                {isRoom && <>
                    <List>
                        <ListItem key={"room-" + roomId} disablePadding sx={{ display: "block" }}>
                            <Box component={NavLink} to={"/" + roomId}>
                                <ListItemButton selected={/^\/\d+.*$/.test(location.pathname)}>
                                    <ListItemIcon>
                                        <FlagIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={roomInfo?.name} />
                                </ListItemButton>
                            </Box>
                            <Typography
                                variant="caption"
                                sx={{display: "block", pl: 2, py: 0.5}}
                            >
                                参加しているメンバー
                            </Typography>
                            <List sx={{ pt: 0, pb: 1, px: 2}}>
                                {roomMembers?.map((item) => (
                                    <ListItem key={item.userId} disablePadding>
                                        <ListItemIcon
                                            sx={{
                                                minWidth: "unset",
                                                width: 24,
                                                mr: 4,
                                                borderRadius: 1,
                                            }}
                                        >
                                            <FaceIcon sx={{ color: (theme) => theme.palette.grey[600] }} />
                                        </ListItemIcon>
                                        <ListItemText primary={item.userName} />
                                    </ListItem>
                                ))}
                            </List>
                        </ListItem>
                    </List>
                    <Divider />
                </>}
                <List>
                    {isRoom &&
                        <ListItem key="topics" disablePadding component={NavLink} to={"/" + roomId + "/topics"}>
                            <ListItemButton selected={location.pathname === "/" + roomId + "/topics"}>
                                <ListItemIcon>
                                    <AssignmentIndIcon />
                                </ListItemIcon>
                                <ListItemText primary="トピック管理" />
                            </ListItemButton>
                        </ListItem>
                    }
                    <List>
                        <ListItem key="uploads" disablePadding component={NavLink} to={"/media"}>
                            <ListItemButton selected={location.pathname === "/media"}>
                                <ListItemIcon>
                                    <SettingsIcon />
                                </ListItemIcon>
                                <ListItemText primary="アップロード管理" />
                            </ListItemButton>
                        </ListItem>
                    </List>
                    <ListItem key="help" disablePadding>
                        <ListItemButton
                            component={NavLink}
                            to={"https://opexpark.notion.site/Connect-4ddf7edf33e145a6b1f079490bde44b8?pvs=4"}
                            target="_blank"
                        >
                            <ListItemIcon>
                                <HelpIcon />
                            </ListItemIcon>
                            <ListItemText primary="ヘルプ" />
                        </ListItemButton>
                    </ListItem>
                </List>
                <Divider />
                <List>
                    <ListItem key="OPExPARK" disablePadding>
                        <ListItemButton
                            component={NavLink}
                            to={"https://service.opexpark.co.jp/gastroenterology"}
                            target={"_blank"}
                        >
                            <ListItemIcon>
                                <MovieCreationIcon />
                            </ListItemIcon>
                            <ListItemText primary={"OPExPARK"} />
                        </ListItemButton>
                    </ListItem>
                </List>
                <Divider />
                <List sx={{
                    p: 2,
                    "a:hover": {
                        color: (theme) => theme.palette.grey[500],
                    }
                }}>
                    {[
                        {
                            text: "会社概要",
                            link: "https://www.opexpark.co.jp/",
                            target: "_blank",

                        },
                        {
                            text: "お問い合わせ",
                            link: "https://share.hsforms.com/1UJHSQWohTBeeS39vdvcNzAclqdg?__hstc=8329069.3c5e7753a8d585c9dd054905bcdaef91.1670995528265.1706088597379.1706498567907.244&__hssc=8329069.3.1706498567907&__hsfp=4077196234",
                            target: "_blank",

                        },
                        {
                            text: "利用規約",
                            link: "https://service.opexpark.co.jp/termsofservice",
                            target: "_blank",
                        },
                        {
                            text: "プライバシーポリシー",
                            link: "https://www.opexpark.co.jp/privacy/",
                            target: "_blank",
                        },
                        {
                            text: "運営からのお知らせ",
                            link: "https://www.notion.so/opexpark/800aa62daf15445e8e6aac2f5d523b77?pvs=4",
                            target: "_blank",
                        },
                    ].map((item) => (
                        <ListItem key={item.text} disablePadding>
                            {item.link ? (
                                <Link
                                    href={item.link}
                                    underline="none"
                                    variant="body2"
                                    target={item.target}
                                >
                                        <ListItemText primary={item.text} />
                                </Link>
                            ) : (
                                    <ListItemText primary={item.text} />
                            )}

                        </ListItem>
                    ))}
                </List>
                <Typography sx={{
                    px: 2,
                    pb: 1,
                    fontSize: "14px",
                    color: (theme) => theme.palette.grey[500],
                }}>
                    Copyright 2023 OPExPARK All Rights Reserved.
                </Typography>
            </Box>
        </>
    );
}